import { elementWasSelected } from "@app/utils/accessibility"
import { isLoungerSelected } from "@app/utils/selectors"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useRef } from "react"

function Lounger({ map, lounger, group, show, onLoungerSelect, tags, loungersRef, hotspots, tabIndex, allLoungersRef, onCheckVisibility, onShiftToElement }) {
	const availableLoungers = useSelector(state => state.order.availableLoungers)
	const isAvailable = availableLoungers.find(e => e.objectIds && e.objectIds?.indexOf(lounger.objectId) !== -1) !== undefined
	const loungerRef = useRef(null)

	const isSelected = useSelector(isLoungerSelected(lounger.objectId))

	const clickLounger = useCallback(() => {
		if (isAvailable) {
			onLoungerSelect(group, lounger.number, lounger.objectId)

			// Restore focus after state update
			setTimeout(() => {
				if (loungerRef.current?.element) {
					loungerRef.current.element.focus()
				}
			}, 100)
		}
	}, [isAvailable, onLoungerSelect, group, lounger.number, lounger.objectId])

	const handleFocus = useCallback(
		event => {
			/**
			 * Only zoom to hotspot if focus came from keyboard (not pointer/mouse)
			 * :focus-visible is applied by browser when focus comes from keyboard
			 */
			if (!event.target.matches(":focus-visible")) {
				return
			}

			// Check if lounger is visible in viewBox
			if (onCheckVisibility && !onCheckVisibility(event.target)) {
				// Shift viewBox to show this lounger
				onShiftToElement?.(event.target)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[lounger.hotspotId, hotspots, onCheckVisibility, onShiftToElement],
	)

	const handleKeyDown = useCallback(
		e => {
			if (elementWasSelected(e)) {
				e.preventDefault()
				clickLounger()
			}
		},
		[clickLounger],
	)

	const getLoungerHref = (isSelected, tags, lounger, isAvailable, group) => {
		if (isSelected) {
			return `#${group.objectGroup}#selected`
		} else if ((tags.length && !lounger.tagIds) || !tags.every(sId => lounger.tagIds.indexOf(sId) !== -1)) {
			return `#${group.objectGroup}#notSelectable`
		} else if (!isAvailable) {
			return `#${group.objectGroup}#disabled`
		}
		return `#${group.objectGroup}`
	}

	const getLoungerAriaLabel = (isSelected, tags, lounger, isAvailable) => {
		const loungerType = lounger.object || "Lounger"

		if (isSelected) {
			return `${loungerType}, selected`
		} else if ((tags.length && !lounger.tagIds) || !tags.every(sId => lounger.tagIds.indexOf(sId) !== -1)) {
			return `${loungerType}, not available for current filters`
		} else if (!isAvailable) {
			return `${loungerType}, unavailable`
		}
		return `${loungerType}, available`
	}

	// Update lounger appearance based on selection state
	useEffect(() => {
		const element = loungerRef.current?.element
		if (!element) return

		const href = getLoungerHref(isSelected, tags, lounger, isAvailable, group)
		element.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", href)

		const ariaLabel = getLoungerAriaLabel(isSelected, tags, lounger, isAvailable, group)
		element.setAttribute("aria-label", ariaLabel)
		element.setAttribute("aria-disabled", !isAvailable ? "true" : "false")

		if (isSelected) {
			element.setAttribute("aria-pressed", "true")
		} else {
			element.setAttribute("aria-pressed", "false")
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelected, isAvailable, tags, lounger.tagIds, group.objectGroup, group.name, lounger.number, lounger.objectId])

	useEffect(() => {
		if (!lounger.x || !lounger.y) return

		let use = document.createElementNS("http://www.w3.org/2000/svg", "use")
		const initialHref = getLoungerHref(isSelected, tags, lounger, isAvailable, group)
		use.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", initialHref)
		use.setAttributeNS(null, "transform", `translate(${lounger.x} ${lounger.y}) rotate(${lounger.rotation ?? 0}, ${group.width / 2} ${group.height / 2})`)
		use.setAttributeNS(null, "width", group.width)
		use.setAttributeNS(null, "height", group.height)
		use.setAttributeNS(null, "data-lounger-object-id", lounger?.objectId)
		use.classList.add(!isAvailable ? "notAvailable" : "available")
		use.style.display = show ? "block" : "none"

		use.setAttribute("tabindex", isAvailable && tabIndex !== undefined ? tabIndex : -1)
		use.setAttribute("role", "button")

		const initialAriaLabel = getLoungerAriaLabel(isSelected, tags, lounger, isAvailable)
		use.setAttribute("aria-label", initialAriaLabel)
		use.setAttribute("aria-disabled", !isAvailable ? "true" : "false")
		use.setAttribute("aria-pressed", isSelected ? "true" : "false")

		if (isAvailable) {
			use.addEventListener("click", clickLounger)
			use.addEventListener("keydown", handleKeyDown)
			use.addEventListener("focus", handleFocus)
		}

		const loungerData = {
			element: use,
			objectId: lounger.objectId,
			hotspotId: lounger.hotspotId,
		}

		loungerRef.current = loungerData

		map.appendChild(use)
		loungersRef.current.push(loungerData)

		if (allLoungersRef) {
			allLoungersRef.current.push(loungerData)
		}

		return () => {
			use.removeEventListener("click", clickLounger)
			use.removeEventListener("keydown", handleKeyDown)
			use.removeEventListener("focus", handleFocus)
			use.remove()

			const index = loungersRef.current.findIndex(x => x?.element === use)

			if (index !== -1) {
				// eslint-disable-next-line react-hooks/exhaustive-deps
				loungersRef.current.splice(index, 1)
			}

			if (allLoungersRef) {
				const globalIndex = allLoungersRef.current.findIndex(x => x?.element === use)
				if (globalIndex !== -1) {
					// eslint-disable-next-line react-hooks/exhaustive-deps
					allLoungersRef.current.splice(globalIndex, 1)
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, lounger, show, isAvailable, tags, handleKeyDown, handleFocus, clickLounger, tabIndex, allLoungersRef])

	useEffect(() => {
		if (loungerRef.current?.element) {
			loungerRef.current.element.style.display = show ? "block" : "none"
		}
	}, [show])

	return null
}

export function LoungerGroup({
	map,
	group,
	show,
	selectable,
	onLoungerSelect,
	mapId,
	tags,
	hotspots,
	tabIndexMap,
	allLoungersRef,
	onCheckVisibility,
	onShiftToElement,
}) {
	const symbolRef = useRef(null)
	const symbolRef2 = useRef(null)
	const symbolRef3 = useRef(null)
	const symbolRef4 = useRef(null)
	const loungersRef = useRef([])

	useEffect(() => {
		if (symbolRef.current) {
			if (!selectable) {
				symbolRef.current.classList.add("notSelectable")
			} else {
				symbolRef.current.classList.remove("notSelectable")
			}
		}
	}, [selectable])

	useEffect(() => {
		let defs = map.querySelector("defs")
		let symbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol")
		symbol.setAttributeNS(null, "viewBox", `${0} ${0} ${group.width} ${group.height}`)
		symbol.setAttributeNS(null, "width", group.width)
		symbol.setAttributeNS(null, "height", group.height)
		symbol.id = group.objectGroup
		symbol.innerHTML = group.svg
		if (!selectable) {
			symbol.classList.add("notSelectable")
		}

		defs.appendChild(symbol)
		symbolRef.current = symbol

		let symbol2 = document.createElementNS("http://www.w3.org/2000/svg", "symbol")
		symbol2.setAttributeNS(null, "viewBox", `${0} ${0} ${group.width} ${40}`)
		symbol2.setAttributeNS(null, "width", group.width)
		symbol2.setAttributeNS(null, "height", group.height)
		symbol2.id = group.objectGroup + "#disabled"
		symbol2.innerHTML = group.svg
		symbol2.classList.add("disabled")

		defs.appendChild(symbol2)
		symbolRef2.current = symbol2

		let symbol3 = document.createElementNS("http://www.w3.org/2000/svg", "symbol")
		symbol3.setAttributeNS(null, "viewBox", `${0} ${0} ${group.width} ${40}`)
		symbol3.setAttributeNS(null, "width", group.width)
		symbol3.setAttributeNS(null, "height", group.height)
		symbol3.id = group.objectGroup + "#selected"
		symbol3.innerHTML = group.svg
		symbol3.classList.add("selected")

		defs.appendChild(symbol3)
		symbolRef3.current = symbol3

		let symbol4 = document.createElementNS("http://www.w3.org/2000/svg", "symbol")
		symbol4.setAttributeNS(null, "viewBox", `${0} ${0} ${group.width} ${40}`)
		symbol4.setAttributeNS(null, "width", group.width)
		symbol4.setAttributeNS(null, "height", group.height)
		symbol4.id = group.objectGroup + "#notSelectable"
		symbol4.innerHTML = group.svg
		symbol4.classList.add("notSelectable")

		defs.appendChild(symbol4)
		symbolRef4.current = symbol4

		return () => {
			symbolRef.current.remove()
			symbolRef2.current.remove()
			symbolRef3.current.remove()
			symbolRef4.current.remove()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map])

	const loungersOfTheSelectedMap = group.objects?.filter(object => object.mapId === mapId) || []

	return loungersOfTheSelectedMap.map(object => {
		return (
			<Lounger
				group={group}
				lounger={object}
				map={map}
				key={object.objectId}
				show={show}
				onLoungerSelect={onLoungerSelect}
				tags={tags}
				loungersRef={loungersRef}
				hotspots={hotspots}
				tabIndex={tabIndexMap ? tabIndexMap[object.objectId] : undefined}
				allLoungersRef={allLoungersRef}
				onCheckVisibility={onCheckVisibility}
				onShiftToElement={onShiftToElement}
			/>
		)
	})
}
